import type { ExportSummary } from '@/lib/api/reports-client.js';
import { getExportStatus } from '@/lib/api/reports-client.js';
import type { ExportListResponse } from '@/lib/api/reports-client.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { EXPORTS_LIST_QUERY_KEY } from './use-exports-list.js';

const POLL_INTERVAL_MS = 5_000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1_000; // 10 minutes

const IN_FLIGHT_STATUSES = new Set<ExportSummary['status']>(['queued', 'running']);

/**
 * Polls in-flight exports (queued|running) and updates the query cache when
 * they reach a terminal state. Fires at 5 s intervals, stops after 10 min.
 *
 * Call this once at the ExportsPage level. It reads the current cache to find
 * which export IDs need polling and updates them in place.
 */
export function useExportStatusPolling(inFlightIds: string[]) {
  const queryClient = useQueryClient();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number | null>(null);

  // W-3: derive a stable primitive key from the array so useEffect only re-runs
  // when the sorted set of IDs actually changes — not on every array identity change.
  const inFlightKey = useMemo(() => [...inFlightIds].sort().join(','), [inFlightIds]);
  const hasInFlight = inFlightKey.length > 0;

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startedAtRef.current = null;
  }, []);

  const patchCache = useCallback(
    (updated: ExportSummary) => {
      const allKeys = queryClient.getQueriesData<ExportListResponse>({
        queryKey: [EXPORTS_LIST_QUERY_KEY],
      });

      for (const [key] of allKeys) {
        queryClient.setQueryData<ExportListResponse>(key as unknown[], (old) => {
          if (old === undefined) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.exportId === updated.exportId ? { ...item, ...updated } : item,
            ),
          };
        });
      }
    },
    [queryClient],
  );

  useEffect(() => {
    if (!hasInFlight) {
      stopPolling();
      return;
    }

    if (intervalRef.current !== null) return; // already polling

    startedAtRef.current = Date.now();

    intervalRef.current = setInterval(async () => {
      // Timeout guard
      if (
        startedAtRef.current !== null &&
        Date.now() - startedAtRef.current > MAX_POLL_DURATION_MS
      ) {
        stopPolling();
        return;
      }

      // Re-read IDs from cache so we stop polling IDs that became terminal
      const allKeys = queryClient.getQueriesData<ExportListResponse>({
        queryKey: [EXPORTS_LIST_QUERY_KEY],
      });

      const currentInFlight = new Set<string>();
      for (const [, data] of allKeys) {
        if (data === undefined) continue;
        for (const item of data.items) {
          if (IN_FLIGHT_STATUSES.has(item.status)) currentInFlight.add(item.exportId);
        }
      }

      if (currentInFlight.size === 0) {
        stopPolling();
        return;
      }

      await Promise.allSettled(
        Array.from(currentInFlight).map(async (id) => {
          try {
            const status = await getExportStatus(id);
            patchCache(status);
          } catch {
            // Swallow individual poll errors — will retry next interval
          }
        }),
      );
    }, POLL_INTERVAL_MS);

    return () => stopPolling();
    // W-4: onSettled in cancel mutation may briefly re-activate polling after an item
    // is cancelled because the list refetch re-adds the ID before the DB status
    // propagates. This is a cosmetic extra poll at worst; deferred to follow-up.
  }, [hasInFlight, stopPolling, patchCache, queryClient]);
}
